import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  CircularProgress,
  Paper,
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Favorite,
  FavoriteBorder,
  BookmarkBorder,
  Bookmark,
  RestaurantMenu,
  AccessTime,
  Clear,
  Add,
  People,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  browseRecipes,
  getRecipeCuisines,
  getRecipeDietaryTags,
  getFamilyDietaryPreferences,
  recordRecipeInteraction,
  deleteRecipeInteraction,
  addRecipeToPlan,
  BrowseRecipe,
  DietaryQuickTag,
} from '../../services/api';

type TabType = 'all' | 'favorites' | 'want_to_try';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];

// Human-friendly labels for the tag names returned by the backend. Anything
// not in this map falls back to a simple title-case of the raw tag_name
// (e.g. "low-carb" → "Low Carb"), so adding a new tag in the DB just works
// without a code change.
const DIETARY_TAG_LABELS: Record<string, string> = {
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  pescatarian: 'Pescatarian',
  'low-carb': 'Low Carb',
  'low-fat': 'Low Fat',
  'low-sugar': 'Low Sugar',
  'low-sodium': 'Low Sodium',
  'heart-healthy': 'Heart Healthy',
  'diabetic-friendly': 'Diabetic Friendly',
  'kid-friendly': 'Kid Friendly',
  halal: 'Halal',
  kosher: 'Kosher',
};

const formatDietaryTagLabel = (raw: string): string =>
  DIETARY_TAG_LABELS[raw] ||
  raw.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const RecipeBrowseScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Page framing depends on membership: Premium users browse for ideas;
  // free users are here to assemble their week.
  const isPremium = user?.membership?.status === 'paid';

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMealType, setSelectedMealType] = useState<string | null>(null);
  const [selectedCuisine, setSelectedCuisine] = useState<string | null>(null);
  // Single-select dietary chip. When set, takes over the include-tag list so
  // "I want vegetarian recipes right now" doesn't get muddled with the user's
  // broader family preferences (which OR-union with the include list on the
  // backend). Deselect (click again) to fall back to family filter behavior.
  const [selectedDietaryTag, setSelectedDietaryTag] = useState<string | null>(null);
  // Dynamic list of dietary chips — populated from /recipes/dietary-tags.
  // Backend returns only positive-identity tags with enough recipe coverage,
  // so this list auto-adapts as the library grows. Empty until fetched.
  const [dietaryChipOptions, setDietaryChipOptions] = useState<DietaryQuickTag[]>([]);
  const [cuisineOptions, setCuisineOptions] = useState<string[]>([]);
  // Default ON so onboarding-collected preferences are surfaced immediately.
  const [familyFilterOn, setFamilyFilterOn] = useState(true);
  const [familyTags, setFamilyTags] = useState<{ include: string[]; exclude: string[] }>({ include: [], exclude: [] });

  const [recipes, setRecipes] = useState<BrowseRecipe[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add to Plan dialog state
  const [addToPlanRecipe, setAddToPlanRecipe] = useState<BrowseRecipe | null>(null);
  const [addToPlanDate, setAddToPlanDate] = useState<string>('');
  const [addToPlanMealType, setAddToPlanMealType] = useState<string>('dinner');
  const [addingToPlan, setAddingToPlan] = useState(false);
  const [addToPlanSuccess, setAddToPlanSuccess] = useState<string | null>(null);
  const [addToPlanError, setAddToPlanError] = useState<string | null>(null);

  const getDateOptions = () => {
    const days: { date: string; label: string }[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : `${dayNames[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
      days.push({ date: dateStr, label });
    }
    return days;
  };

  const openAddToPlan = (e: React.MouseEvent, recipe: BrowseRecipe) => {
    e.stopPropagation();
    setAddToPlanRecipe(recipe);
    setAddToPlanDate(getDateOptions()[0].date);
    setAddToPlanMealType(recipe.meal_type || 'dinner');
    setAddToPlanError(null);
  };

  const handleAddToPlan = async () => {
    if (!addToPlanRecipe || !addToPlanDate) return;
    setAddingToPlan(true);
    setAddToPlanError(null);
    try {
      const result = await addRecipeToPlan({
        recipe_id: addToPlanRecipe.recipe_id,
        meal_type: addToPlanMealType,
        assigned_date: addToPlanDate,
      });
      setAddToPlanSuccess(`Added ${addToPlanRecipe.name} for ${addToPlanMealType} on ${addToPlanDate} (${result.servings} servings).`);
      setAddToPlanRecipe(null);
    } catch (err: any) {
      setAddToPlanError(err.response?.data?.message || 'Failed to add recipe to plan.');
    } finally {
      setAddingToPlan(false);
    }
  };

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load cuisines + family preferences once
  useEffect(() => {
    getRecipeCuisines().then(setCuisineOptions).catch(() => {});
    getRecipeDietaryTags().then(setDietaryChipOptions).catch(() => {});
    getFamilyDietaryPreferences().then(setFamilyTags).catch(() => {});
  }, []);

  const fetchRecipes = useCallback(async (pageNum: number, reset: boolean, queryOverride?: string) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    try {
      const q = queryOverride !== undefined ? queryOverride : searchQuery;
      const interactionParam: 'favorite' | 'want_to_try' | undefined =
        activeTab === 'favorites' ? 'favorite' : activeTab === 'want_to_try' ? 'want_to_try' : undefined;
      // Explicit dietary chip takes priority: when the user picks "Vegetarian"
      // they want vegetarian results, not vegetarian UNION whatever family
      // preferences would otherwise include. Excludes (allergies) still apply
      // regardless — you never want to serve a nut recipe to a nut-allergic
      // family just because someone tapped "Keto".
      const includeTags = selectedDietaryTag
        ? [selectedDietaryTag]
        : (familyFilterOn && familyTags.include.length > 0 ? familyTags.include : undefined);
      const result = await browseRecipes({
        query: q || undefined,
        mealType: selectedMealType || undefined,
        cuisine: selectedCuisine || undefined,
        page: pageNum,
        limit: 24,
        interaction: interactionParam,
        dietaryTags: includeTags,
        excludeTags: familyFilterOn && familyTags.exclude.length > 0 ? familyTags.exclude : undefined,
      });
      if (reset) setRecipes(result.recipes);
      else setRecipes(prev => [...prev, ...result.recipes]);
      setPage(pageNum);
      setTotalPages(result.totalPages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, selectedMealType, selectedCuisine, activeTab, familyFilterOn, familyTags, selectedDietaryTag]);

  // Re-fetch when filters/tab change.
  // familyTags included so default-on filter re-fires after async load.
  // selectedDietaryTag included so tapping a chip triggers an immediate
  // refetch (without this, the chip appears "selected" but results don't
  // update — the useCallback picks it up on the next render but nothing
  // fires it).
  useEffect(() => {
    fetchRecipes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMealType, selectedCuisine, familyFilterOn, familyTags, selectedDietaryTag]);

  const handleSearchChange = (text: string) => {
    setSearchQuery(text);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchRecipes(1, true, text);
    }, 300);
  };

  const handleMealTypeClick = (type: string) => {
    setSelectedMealType(prev => (prev === type ? null : type));
  };

  const handleCuisineClick = (c: string) => {
    setSelectedCuisine(prev => (prev === c ? null : c));
  };

  const handleTabChange = (_: any, newTab: TabType) => {
    setActiveTab(newTab);
    setSearchQuery('');
    setSelectedMealType(null);
    setSelectedCuisine(null);
  };

  const handleLoadMore = () => {
    if (!loadingMore && page < totalPages) {
      fetchRecipes(page + 1, false);
    }
  };

  const handleInteraction = async (e: React.MouseEvent, recipeId: number, action: 'favorite' | 'want_to_try') => {
    e.stopPropagation();
    const recipe = recipes.find(r => r.recipe_id === recipeId);
    if (!recipe) return;

    const currentInteraction = recipe.user_interaction;
    const isRemoving = currentInteraction === action;

    // Optimistic update
    setRecipes(prev =>
      prev
        .map(r => {
          if (r.recipe_id !== recipeId) return r;
          return { ...r, user_interaction: isRemoving ? null : action };
        })
        .filter(r => {
          // On favorites/want_to_try tabs, remove cards when un-toggled
          if (activeTab !== 'all' && r.recipe_id === recipeId && isRemoving) return false;
          return true;
        })
    );

    try {
      if (isRemoving) {
        await deleteRecipeInteraction(recipeId);
      } else {
        await recordRecipeInteraction(recipeId, action);
      }
    } catch {
      // Revert on failure
      setRecipes(prev =>
        prev.map(r => (r.recipe_id === recipeId ? { ...r, user_interaction: currentInteraction } : r))
      );
    }
  };

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        {isPremium ? 'Recipes' : 'Assemble Your Meal Plan'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {isPremium
          ? 'Browse our full recipe library, save favorites, and discover new ideas.'
          : 'Pick recipes you love and add them to your week. Use the filters to find meals that fit your family.'}
      </Typography>

      {/* Search bar. Placeholder is explicit about scope — otherwise users
          typed "vegetarian" here and got 0 results because dietary tags don't
          appear in recipe names/ingredients. Discoverability for those tags
          lives in the chip strip below. */}
      <TextField
        fullWidth
        placeholder="Search by food or ingredient…"
        value={searchQuery}
        onChange={(e) => handleSearchChange(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => handleSearchChange('')}>
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Dietary quick-select chips. Horizontally scrolls on narrow viewports
          so we don't push the recipe grid off the fold. Single-select — the
          selected chip overrides any family-preference include tags for the
          duration of this browse. Source list comes from /recipes/dietary-tags
          which filters out negation tags and sparse ones. */}
      {dietaryChipOptions.length > 0 && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 0.5,
            // Hide the scrollbar visually; horizontal scroll still works.
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {dietaryChipOptions.map(chip => (
            <Chip
              key={chip.tag_name}
              label={formatDietaryTagLabel(chip.tag_name)}
              onClick={() =>
                setSelectedDietaryTag(prev => (prev === chip.tag_name ? null : chip.tag_name))
              }
              color={selectedDietaryTag === chip.tag_name ? 'primary' : 'default'}
              variant={selectedDietaryTag === chip.tag_name ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      {/* Sub-tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="All" value="all" />
        <Tab label="Favorites" value="favorites" />
        <Tab label="Want to Try" value="want_to_try" />
      </Tabs>

      {/* Filter chips */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {(familyTags.include.length > 0 || familyTags.exclude.length > 0) && (() => {
          // Label + tooltip adapt to how much profile data the user has filled in.
          // Sparse = only allergies (free users from slim onboarding); Full = restrictions/medical/goals too.
          const isSparse = familyTags.include.length === 0 && familyTags.exclude.length > 0;
          const chipLabel = isSparse ? 'My Allergies' : 'My Preferences';
          const tooltipText = isSparse
            ? 'Set more preferences in Account'
            : "Edit preferences in Account";
          return (
            <>
              <Tooltip title={tooltipText}>
                <Chip
                  icon={<People fontSize="small" />}
                  label={chipLabel}
                  onClick={() => setFamilyFilterOn(prev => !prev)}
                  color={familyFilterOn ? 'primary' : 'default'}
                  variant={familyFilterOn ? 'filled' : 'outlined'}
                />
              </Tooltip>
              <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', height: 24, mx: 1 }} />
            </>
          );
        })()}
        {MEAL_TYPES.map(type => (
          <Chip
            key={type}
            label={capitalize(type)}
            onClick={() => handleMealTypeClick(type)}
            color={selectedMealType === type ? 'primary' : 'default'}
            variant={selectedMealType === type ? 'filled' : 'outlined'}
          />
        ))}
        {cuisineOptions.length > 0 && (
          <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', height: 24, mx: 1 }} />
        )}
        {cuisineOptions.map(c => (
          <Chip
            key={c}
            label={capitalize(c)}
            onClick={() => handleCuisineClick(c)}
            color={selectedCuisine === c ? 'primary' : 'default'}
            variant={selectedCuisine === c ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading / empty / grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : recipes.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <RestaurantMenu sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {activeTab === 'favorites' ? 'No favorites yet' : activeTab === 'want_to_try' ? 'No saved recipes' : 'No recipes found'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'favorites' ? 'Tap the heart on any recipe to save it here.' :
              activeTab === 'want_to_try' ? 'Tap the bookmark on any recipe to save it here.' :
                'Try adjusting your search or filters.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            {recipes.map(recipe => {
              const isFavorite = recipe.user_interaction === 'favorite';
              const isWantToTry = recipe.user_interaction === 'want_to_try';
              return (
                <Card
                  key={recipe.recipe_id}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'transform 0.15s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                  }}
                  onClick={() => navigate(`/meal/${recipe.recipe_id}`)}
                >
                  {recipe.image_url ? (
                    <CardMedia component="img" height="160" image={recipe.image_url} alt={recipe.name} />
                  ) : (
                    <Box sx={{ height: 160, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RestaurantMenu sx={{ fontSize: 40, color: 'text.disabled' }} />
                    </Box>
                  )}

                  {/* Add to Plan button (top-left) */}
                  <Tooltip title="Add to meal plan">
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        bgcolor: 'primary.main',
                        color: 'white',
                        boxShadow: 2,
                        '&:hover': { bgcolor: 'primary.dark' },
                      }}
                      onClick={(e) => openAddToPlan(e, recipe)}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Interaction overlay */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 2, p: 0.5 }}>
                    <IconButton
                      size="small"
                      sx={{ color: 'white', p: 0.5 }}
                      onClick={(e) => handleInteraction(e, recipe.recipe_id, 'favorite')}
                    >
                      {isFavorite ? <Favorite fontSize="small" sx={{ color: '#d32f2f' }} /> : <FavoriteBorder fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: 'white', p: 0.5 }}
                      onClick={(e) => handleInteraction(e, recipe.recipe_id, 'want_to_try')}
                    >
                      {isWantToTry ? <Bookmark fontSize="small" sx={{ color: '#F78764' }} /> : <BookmarkBorder fontSize="small" />}
                    </IconButton>
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="600" sx={{ lineHeight: 1.2, mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {recipe.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {(recipe.total_time || recipe.prep_time) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {recipe.total_time || recipe.prep_time} min
                          </Typography>
                        </Box>
                      )}
                      {recipe.meal_type && (
                        <Typography variant="caption" color="text.secondary">
                          {capitalize(recipe.meal_type)}
                        </Typography>
                      )}
                    </Stack>
                    {recipe.tags && recipe.tags.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {recipe.tags.slice(0, 2).map((tag, idx) => (
                          <Chip key={`${tag}-${idx}`} label={capitalize(tag)} size="small" sx={{ height: 20, fontSize: 10 }} />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Load more */}
          {page < totalPages && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
                size="large"
              >
                {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
              </Button>
            </Box>
          )}

          {/* Contact prompt */}
          <Paper sx={{ mt: 4, p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" fontWeight="600">
              Don't see something you want?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Message us and we can add a recipe to our database for you!
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
          </Paper>
        </>
      )}

      {/* Success snackbar */}
      {addToPlanSuccess && (
        <Alert
          severity="success"
          onClose={() => setAddToPlanSuccess(null)}
          sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, minWidth: 320, boxShadow: 3 }}
        >
          {addToPlanSuccess}
        </Alert>
      )}

      {/* Add to Plan Dialog */}
      <Dialog open={!!addToPlanRecipe} onClose={() => setAddToPlanRecipe(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Meal Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {addToPlanRecipe?.name}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Meal Type</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {['breakfast', 'lunch', 'dinner'].map(type => (
              <Chip
                key={type}
                label={capitalize(type)}
                onClick={() => setAddToPlanMealType(type)}
                color={addToPlanMealType === type ? 'primary' : 'default'}
                variant={addToPlanMealType === type ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Date</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {getDateOptions().map(opt => (
              <Chip
                key={opt.date}
                label={opt.label}
                onClick={() => setAddToPlanDate(opt.date)}
                color={addToPlanDate === opt.date ? 'primary' : 'default'}
                variant={addToPlanDate === opt.date ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            Servings will be automatically set based on your family's meal schedule.
          </Typography>

          {addToPlanError && (
            <Alert severity="error" sx={{ mt: 2 }}>{addToPlanError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToPlanRecipe(null)} disabled={addingToPlan}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToPlan}
            disabled={!addToPlanDate || addingToPlan}
          >
            {addingToPlan ? <CircularProgress size={20} /> : 'Add to Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecipeBrowseScreen;
